// src/utils/deviceFingerprint.js
//
// FAZ 5 GELİŞTİRME 1 — Device fingerprint (Emir 0.3 tamamlama)
// ──────────────────────────────────────────────────────────────────────────
// 5-ajan audit (Emir 0.3) bulgusu: anti-gaming yalnız IP+UID bazlıydı.
// Aynı kullanıcı VPN + multi-account ile IP/UID rotasyonu yaparak rate
// limit'i kolayca atlatabiliyordu. Device-level fingerprint bot ağlarını
// (özellikle headless browser farm'larını) yakalamak için ek bir katman.
//
// Üretilen fingerprint:
//   SHA-256(
//     WebGL canvas hash +
//     User-Agent +
//     screen.width × screen.height +
//     Intl.DateTimeFormat().resolvedOptions().timeZone +
//     AudioContext.sampleRate × AudioContext.channelCount
//   ).slice(0, 16) → 16 karakter hex (privacy-aware: tam SHA-256 değil)
//
// Cache: sessionStorage["mlv3.dfp"] — session boyunca tek hesaplama
// (computational cost ~ms; tekrarlamak gereksiz).
//
// Privacy:
//   - 16 karakter hex → 2^64 entropy; aynı device tekrar tespit edilebilir
//     ama reverse engineering yapılamaz (one-way hash).
//   - PII içermez (uid, email, IP yok); sadece hardware/browser sinyalleri.
//   - Cross-domain tracking yapamaz (her domain için yeni sessionStorage).
//
// Fail-safe:
//   - SubtleCrypto unsupported → "unknown_" + Date.now().toString(36)
//   - Herhangi bir sub-component (WebGL, AudioContext) hata fırlatırsa
//     o bileşen "" olarak atlanır; fingerprint kalan veriyle hesaplanır.
//   - Server tarafında fallback değerler de geçerli; "unknown_*"
//     pattern'i ile audit'te ayırt edilebilir.

const CACHE_KEY = "mlv3.dfp";
const FALLBACK_PREFIX = "unknown_";

/**
 * WebGL renderer + vendor stringini SHA-256 öncesi parça olarak topla.
 * Headless / mock GPU'da boş döner; bot tespitinin önemli sinyallerinden.
 */
function getWebGLSignal() {
  try {
    if (typeof document === "undefined") return "";
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return "no-webgl";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    if (ext) {
      const vendor = String(
        gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) || "",
      );
      const renderer = String(
        gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || "",
      );
      return `${vendor}|${renderer}`;
    }
    // Fallback: standart gl.getParameter (genelde maskelidir)
    const vendor = String(gl.getParameter(gl.VENDOR) || "");
    const renderer = String(gl.getParameter(gl.RENDERER) || "");
    return `${vendor}|${renderer}`;
  } catch (_) {
    return "";
  }
}

/**
 * AudioContext sampleRate + channelCount + maxChannelCount kombinasyonu.
 * Headless browser'larda farklılaşır (özellikle puppeteer/playwright).
 */
function getAudioSignal() {
  try {
    if (typeof window === "undefined") return "";
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return "no-ac";
    // OfflineAudioContext daha hafif (gerçek output stream açmaz)
    const Off =
      window.OfflineAudioContext || (window as any).webkitOfflineAudioContext;
    if (Off) {
      // `close` OfflineAudioContext lib tipinde YOK (AudioContext'te var); bazı
      // tarayıcılar yine de sağlıyor — gövdenin `?.` guard'ı zaten bunun için.
      // ⚠️ Tip, ÇAĞRI YERİNDE `as` ile değil BİLDİRİMDE açıldı: `(ctx as X).close?.()`
      //    yazımı TS'e fazladan bir geçici değişken ürettiriyordu
      //    (`(_d = (_c = ctx).close)`) → derlenen JS ŞEKLİ değişiyordu. Bildirime
      //    yazınca emit ORİJİNALİYLE BAYT BAYT aynı kalıyor.
      const ctx: OfflineAudioContext & { close?: () => void } = new Off(1, 1, 44100);
      const sr = ctx.sampleRate;
      const dest = ctx.destination;
      const ch = dest?.channelCount ?? 0;
      const maxCh = dest?.maxChannelCount ?? 0;
      // `OfflineAudioContext` lib.dom'da `close()` İLAN ETMİYOR (yalnız
      // `BaseAudioContext`in alt sınıfı `AudioContext`te var). Gövde bunu zaten
      // `?.()` + try/catch ile karşılıyor — `as` yalnız o gerçeği yazıya döker.
      try { (ctx as { close?: () => void }).close?.(); } catch (_) {}
      return `${sr}|${ch}|${maxCh}`;
    }
    const ctx = new Ctx();
    const sr = ctx.sampleRate;
    const ch = ctx.destination?.channelCount ?? 0;
    const maxCh = ctx.destination?.maxChannelCount ?? 0;
    try { ctx.close?.(); } catch (_) {}
    return `${sr}|${ch}|${maxCh}`;
  } catch (_) {
    return "";
  }
}

function getScreenSignal() {
  try {
    if (typeof window === "undefined" || !window.screen) return "";
    const w = window.screen.width || 0;
    const h = window.screen.height || 0;
    const dpr = window.devicePixelRatio || 1;
    return `${w}x${h}@${dpr}`;
  } catch (_) {
    return "";
  }
}

function getTimezoneSignal() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (_) {
    return "";
  }
}

function getUserAgent() {
  try {
    if (typeof navigator === "undefined") return "";
    return navigator.userAgent || "";
  } catch (_) {
    return "";
  }
}

/**
 * SubtleCrypto SHA-256 ve hex encode. Buffer<32B> → "0a1b2c..." (64 char).
 * İlk 16 karakter döndürülür (privacy + index/key uzunluğu kontrolü).
 */
async function sha256Hex16(text: string): Promise<string | null> {
  if (
    typeof crypto === "undefined" ||
    !crypto.subtle ||
    typeof TextEncoder === "undefined"
  ) {
    return null;
  }
  try {
    const enc = new TextEncoder().encode(text);
    const buf = await crypto.subtle.digest("SHA-256", enc);
    const arr = Array.from(new Uint8Array(buf));
    const hex = arr.map((b) => b.toString(16).padStart(2, "0")).join("");
    return hex.slice(0, 16);
  } catch (_) {
    return null;
  }
}

let _inflight: Promise<string> | null = null;

/**
 * Cihaz parmak izi — async. sessionStorage cache; ikinci çağrı O(1).
 *
 * @returns {Promise<string>} 16 karakter hex VEYA "unknown_<base36>".
 */
export async function getDeviceFingerprint() {
  // Cache hit
  try {
    if (typeof window !== "undefined") {
      const cached = window.sessionStorage?.getItem(CACHE_KEY);
      if (cached && typeof cached === "string" && cached.length > 0) {
        return cached;
      }
    }
  } catch (_) {}

  // In-flight: aynı render cycle'da çoklu çağrı tek promise'a tied
  if (_inflight) return _inflight;

  _inflight = (async () => {
    const parts = [
      getWebGLSignal(),
      getUserAgent(),
      getScreenSignal(),
      getTimezoneSignal(),
      getAudioSignal(),
    ].join("||");

    const hashed = await sha256Hex16(parts);
    const finalDfp =
      hashed || FALLBACK_PREFIX + Date.now().toString(36);

    try {
      if (typeof window !== "undefined") {
        window.sessionStorage?.setItem(CACHE_KEY, finalDfp);
      }
    } catch (_) {}
    _inflight = null;
    return finalDfp;
  })();

  return _inflight;
}
